// Settings component
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings as SettingsIcon, Building2, Mail, CreditCard, User, ChevronRight, Moon, Sun, Shield, Wrench } from 'lucide-react';
import ThemeToggle from '../theme/ThemeToggle';
import { useTheme } from '../theme/ThemeContext';
import { useAuth } from '../auth/AuthContext';

const Settings = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();

  const settingsSections = [
    {
      icon: User,
      title: 'User Profile',
      description: 'Manage your personal information and contact details',
      path: '/settings/profile'
    },
    {
      icon: Building2,
      title: 'Organization',
      description: 'Manage your organization and team members',
      path: '/settings/organization'
    },
    {
      icon: CreditCard,
      title: 'Stripe Payments',
      description: 'Configure payment processing for class enrollments',
      path: '/settings/stripe'
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'Configure email settings',
      path: '/settings/email'
    },
    {
      icon: Wrench,
      title: 'Tools',
      description: 'Utilities and data management tools',
      path: '/settings/tools'
    },
    ...(user?.role === 'admin' ? [{
      icon: Shield,
      title: 'Admin Tools',
      description: 'Administrative tools and system management',
      path: '/admin'
    }] : [])
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center space-x-3">
          <SettingsIcon className="w-8 h-8 text-[#7A99D8]" />
          <div>
            <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Settings</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Manage your application preferences</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Theme Toggle Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                  {isDarkMode ? (
                    <Moon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  ) : (
                    <Sun className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Appearance
                  </h3>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Switch between light and dark mode
                  </p>
                </div>
              </div>
              <ThemeToggle />
            </div>
          </div>

          {/* Other Settings Sections */}
          {settingsSections.map((section) => {
            const Icon = section.icon;
            return (
              <button
                key={section.path}
                onClick={() => navigate(section.path)}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 hover:border-indigo-500 dark:hover:border-indigo-400 transition-colors text-left group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4">
                    <div className="p-3 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                      <Icon className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {section.title}
                      </h3>
                      <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {section.description}
                      </p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Settings;